import type {
  ActiveGame,
  ConnectTokenResponse,
  CreditSpend,
  CreditSpendsResponse,
  Friend,
  GameSummary,
  ImportAvailability,
  Leaderboard,
  Lobby,
  LobbyInvitation,
  LobbyTalkId,
  Me,
  OfflineGameUpload,
  OfflineSyncResponse,
  Platform,
  PlayerPreferences,
  PlayerReport,
  Presence,
  Profile,
  PublicStatsResponse,
  ReplayResponse,
  Ruleset,
  Session,
  StatsHistory,
  StatsResponse,
  SubscriptionInterval,
  SubscriptionPlan,
  SubscriptionUrlResponse,
  Ticket,
} from '@cribbage/protocol';
import { ClientAnalytics, type AnalyticsPermission } from './analytics.js';

export type { ReplayResponse };

/** What the player agreed to, and when, as the server records it (D10). */
export interface PolicyAcceptance {
  readonly terms_version: string;
  readonly privacy_version: string;
  /** The tap, as an ISO timestamp. The server clamps one from the future. */
  readonly accepted_at: string;
  readonly platform: Platform;
}

/**
 * The Rails half of the backend, as seen from the client.
 *
 * Thin on purpose: one `request` helper, one error type, and a method per
 * endpoint. Response shapes come from `@cribbage/protocol`, which is the same
 * definition Rails validates its own responses against — so a field renamed on
 * either side is a type error here rather than an `undefined` at runtime.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

export interface ApiOptions {
  readonly baseUrl?: string;
  readonly fetcher?: typeof fetch;
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly token?: string | null;
  // `| undefined` so a caller can pass its own optional signal straight through.
  readonly signal?: AbortSignal | undefined;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetcher: typeof fetch;
  private token: string | null = null;
  readonly analytics: ClientAnalytics;

  constructor(options: ApiOptions = {}) {
    this.baseUrl = (options.baseUrl ?? inferBaseUrl()).replace(/\/$/, '');
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
    this.analytics = new ClientAnalytics((body, token) =>
      this.request('/api/v1/me/analytics', { method: 'POST', body, token }),
    );
  }

  /** Regional policy integration point. Defaults to denied until supplied. */
  setAnalyticsPermission(permission: AnalyticsPermission): void {
    this.analytics.setPermission(permission);
  }

  setToken(token: string | null): void {
    this.analytics.retainToken(token);
    this.token = token;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = options.token === undefined ? this.token : options.token;
    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (token !== null && token !== '') headers.authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await this.fetcher(`${this.baseUrl}${path}`, {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? null : JSON.stringify(options.body),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (cause) {
      // A dropped connection is not a 500; the UI wants to say "offline".
      throw new ApiError(0, 'network', cause instanceof Error ? cause.message : 'network error');
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload: unknown = text === '' ? null : safeParse(text);

    if (!response.ok) {
      const error = (
        payload as {
          error?: { code?: string; message?: string; details?: Record<string, string[]> };
        }
      )?.error;
      throw new ApiError(
        response.status,
        error?.code ?? 'error',
        error?.message ?? `request failed (${String(response.status)})`,
        error?.details,
      );
    }
    return payload as T;
  }

  /* ------------------------------------------------------------------ auth */

  /**
   * Mint a device, an anonymous player, and a session for it.
   *
   * Unauthenticated, and never idempotent: the token it answers with is the
   * only credential this device has, so there is no id the client could send
   * to be handed the same session twice. Losing the token loses the anonymous
   * player, which is exactly what clearing the app's data does.
   */
  async registerDevice(platform: Platform, acceptance: PolicyAcceptance): Promise<Session> {
    const session = await this.request<Session>('/api/v1/devices', {
      method: 'POST',
      body: { platform, acceptance },
      token: null,
    });
    this.analytics.identify(session.user, session.token);
    return session;
  }

  /**
   * Record the device's agreement for the player this session holds: after a
   * terms change, and after signing in to another player on an agreed device.
   * A registration carries its own, so a new guest never needs this.
   */
  async recordAcceptance(acceptance: PolicyAcceptance): Promise<void> {
    await this.request('/api/v1/policy_acceptances', { method: 'POST', body: acceptance });
  }

  /** Bind an APNs or FCM handle to the session's device. */
  registerPushToken(platform: Platform, token: string): Promise<void> {
    return this.request('/api/v1/devices/push_token', {
      method: 'POST',
      body: { platform, token },
    });
  }

  /*
   * Register, sign in, and OAuth all go out under the device's current token.
   *
   * The server switches the device from one player to another in a single
   * call: it revokes the session in the bearer header and issues one for the
   * account's player. Registration also adopts the device's local player, so
   * everything played before signing up is already the new account's without
   * anything moving.
   */
  async register(email: string, password: string, displayName: string): Promise<Session> {
    this.analytics.track('signup_started');
    const session = await this.request<Session>('/api/v1/auth/register', {
      method: 'POST',
      body: { email, password, display_name: displayName },
    });
    this.analytics.session(session, 'account_created');
    return session;
  }

  async login(email: string, password: string): Promise<Session> {
    const session = await this.request<Session>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    this.analytics.session(session, 'signed_in');
    return session;
  }

  async oauth(
    provider: 'apple' | 'google',
    idToken: string,
    displayName?: string,
  ): Promise<Session> {
    const session = await this.request<Session>('/api/v1/auth/oauth', {
      method: 'POST',
      body: { provider, id_token: idToken, display_name: displayName },
    });
    this.analytics.session(session, 'oauth_completed');
    return session;
  }

  /**
   * End the account session and come back as this device's local player.
   *
   * The server answers with that local session, so signing out never leaves
   * the app without a player. A session with no device behind it (a private
   * window) has no local player to fall back to and answers 204, which arrives
   * here as undefined.
   */
  async logout(): Promise<Session | undefined> {
    const session = await this.request<Session | undefined>('/api/v1/auth/logout', {
      method: 'DELETE',
    });
    this.analytics.forget();
    if (session !== undefined) this.analytics.identify(session.user, session.token);
    return session;
  }

  requestPasswordReset(email: string): Promise<void> {
    return this.request('/api/v1/auth/password_reset', {
      method: 'POST',
      body: { email },
      token: null,
    });
  }

  /**
   * Sets the password the reset email's link was for. It signs nobody in: the
   * server revokes every session the account had, this device's included if
   * it held one, and the player signs in with the new password.
   */
  confirmPasswordReset(token: string, password: string): Promise<void> {
    return this.request('/api/v1/auth/password_reset', {
      method: 'PATCH',
      body: { token, password },
      token: null,
    });
  }

  /* --------------------------------------------------------------- profile */

  async me(): Promise<Me> {
    const token = this.token;
    const user = await this.request<Me>('/api/v1/me');
    if (token !== null && token === this.token) this.analytics.identify(user, token);
    return user;
  }

  updateMe(changes: {
    display_name?: string;
    /** Catalog ID only. The server rejects IDs unavailable to this account. */
    avatar_id?: string | null;
    /** Explicit null clears it — "no country" is a choice, not an omission. */
    country?: string | null;
  }): Promise<Me> {
    return this.request('/api/v1/me', { method: 'PATCH', body: changes });
  }

  /** Needs the current password: a new sign-in address is a way to be locked out. */
  changeEmail(email: string, currentPassword: string): Promise<Me> {
    return this.request('/api/v1/me/email', {
      method: 'PATCH',
      body: { email, current_password: currentPassword },
    });
  }

  changePassword(currentPassword: string, password: string): Promise<Me> {
    return this.request('/api/v1/me/password', {
      method: 'PATCH',
      body: { current_password: currentPassword, password },
    });
  }

  /**
   * Deletes the signed-in player's data. An account answers 204, which arrives
   * here as undefined. A guest's data is erased and the server answers with the
   * session of the fresh guest it gave this install, so the device is never left
   * without a player.
   */
  async deleteAccount(): Promise<Session | undefined> {
    const token = this.token;
    const session = await this.request<Session | undefined>('/api/v1/me', { method: 'DELETE' });
    // Like `me`: a sign-in that landed while this was in flight keeps its own identity.
    if (session !== undefined && token === this.token) {
      this.analytics.forget();
      this.analytics.identify(session.user, session.token);
    }
    return session;
  }

  /**
   * Whether this device played games before the account signed in.
   *
   * Answered against the device's local player, and false once that player has
   * been imported or the offer declined, so the question is asked once.
   */
  importAvailability(signal?: AbortSignal): Promise<ImportAvailability> {
    return this.request('/api/v1/me/import', {
      signal,
    });
  }

  /** Move the device's local player onto the account, then forget it. */
  acceptImport(): Promise<void> {
    return this.request('/api/v1/me/import', { method: 'POST' });
  }

  /** Never ask again for this local player. */
  declineImport(): Promise<void> {
    return this.request('/api/v1/me/import', { method: 'DELETE' });
  }

  /**
   * The Cross-Cribbage walkthrough is finished. Safe to send again; answers
   * with the whole document, so `cross_invitation` comes back false.
   */
  completeCrossTour(): Promise<Me> {
    return this.request('/api/v1/me/cross_tour', { method: 'POST' });
  }

  /* ---------------------------------------------------------- subscription */

  /**
   * Stripe Checkout for one plan at `price`, the `web_prices` id the page
   * showed. The web sells yearly only; the server refuses `month`, and answers
   * 409 `price_changed` for a price replaced since.
   */
  startSubscriptionCheckout(
    plan: SubscriptionPlan,
    interval: SubscriptionInterval,
    price: string,
  ): Promise<SubscriptionUrlResponse> {
    return this.request('/api/v1/me/subscription/checkout', {
      method: 'POST',
      body: { plan, interval, price },
    });
  }

  /**
   * Moves a Stripe member onto another plan. An upgrade applies once its
   * difference is paid, and a downgrade waits for renewal (`pending_change`).
   * Answers with `/me`, or, when the card needs the player to approve an
   * upgrade's charge, with the invoice page to pay it on. `price` is the
   * `web_prices` id the page showed, refused as 409 `price_changed` once
   * replaced.
   */
  changeSubscriptionPlan(
    plan: SubscriptionPlan,
    interval: SubscriptionInterval,
    price: string,
  ): Promise<Me | SubscriptionUrlResponse> {
    return this.request('/api/v1/me/subscription', {
      method: 'PATCH',
      body: { plan, interval, price },
    });
  }

  /** Stripe's Customer Portal, for renewal, the card on file and cancellation. */
  manageSubscription(): Promise<SubscriptionUrlResponse> {
    return this.request('/api/v1/me/subscription/manage', { method: 'POST' });
  }

  /**
   * Hints and reviews spent on this device, possibly offline. Every well-formed
   * spend is taken; the answer is the balance after them.
   */
  spendCredits(
    spends: readonly CreditSpend[],
    signal?: AbortSignal,
  ): Promise<CreditSpendsResponse> {
    return this.request('/api/v1/me/credit_spends', {
      method: 'POST',
      body: { spends },
      signal,
    });
  }

  /** Saves the Game settings named here and keeps the rest. Answers with the whole record. */
  updatePreferences(preferences: PlayerPreferences, signal?: AbortSignal): Promise<Me> {
    return this.request('/api/v1/me/preferences', {
      method: 'PATCH',
      body: { preferences },
      signal,
    });
  }

  /**
   * How many people have the app open. Also the heartbeat: a signed-in call
   * stamps this player as here, which is what keeps a player mid-game on the
   * websocket counted.
   */
  presence(): Promise<Presence> {
    return this.request('/api/v1/presence');
  }

  history(cursor?: string): Promise<{ games: GameSummary[]; next_cursor: string | null }> {
    const query = cursor === undefined ? '' : `?cursor=${encodeURIComponent(cursor)}`;
    return this.request(`/api/v1/me/games${query}`);
  }

  /**
   * The online game this player can go back to, with its live score, or null.
   *
   * Rails answers 204 when there is none, when the game's room is gone, or when
   * the game service does not answer in time; all three read as "nothing to
   * resume" here.
   */
  async activeGame(signal?: AbortSignal): Promise<ActiveGame | null> {
    const active = await this.request<ActiveGame | undefined>('/api/v1/me/active_game', {
      signal,
    });
    return active ?? null;
  }

  /** `opponents` is `human` or `bot`; left out, games against both are counted. */
  stats(mode = 'standard', source = 'online', opponents?: string): Promise<StatsResponse> {
    const query = new URLSearchParams({ mode, source });
    if (opponents !== undefined) query.set('opponents', opponents);
    return this.request(`/api/v1/me/stats?${query.toString()}`);
  }

  /** The same numbers over time: per-game points, the hand distribution, rating. */
  statsHistory(
    options: {
      mode?: string;
      format?: string;
      window?: string;
      source?: string;
      opponents?: string;
    } = {},
  ): Promise<StatsHistory> {
    const query = new URLSearchParams({
      mode: options.mode ?? 'standard',
      window: options.window ?? 'month',
      source: options.source ?? 'online',
    });
    if (options.format !== undefined) query.set('format', options.format);
    if (options.opponents !== undefined) query.set('opponents', options.opponents);
    return this.request(`/api/v1/me/stats/history?${query.toString()}`);
  }

  /**
   * Hand finished offline games over to be counted.
   *
   * The body carries `(config, seed, moves)` and nothing else — the server
   * re-runs the moves through the real engine and derives every number itself,
   * which is what makes it safe to accept. `deferred` games were not reachable
   * and must be kept for another attempt; `rejected` ones never will be.
   */
  syncOfflineGames(
    games: readonly OfflineGameUpload[],
    signal?: AbortSignal,
  ): Promise<OfflineSyncResponse> {
    return this.request('/api/v1/me/offline_games', {
      method: 'POST',
      body: { games },
      signal,
    });
  }

  leaderboard(mode = 'standard', format = '2p'): Promise<Leaderboard> {
    return this.request(`/api/v1/leaderboards?mode=${mode}&format=${format}`);
  }

  /* --------------------------------------------------------------- players */

  /**
   * Another player's public profile, with how the caller stands with them in
   * `relationship`. An anonymized player answers 404.
   */
  profile(id: string, signal?: AbortSignal): Promise<Profile> {
    return this.request(`/api/v1/profiles/${id}`, { signal });
  }

  /**
   * Another player's record, the Seat peek's half of the Stats screen. Leave
   * `mode` out for both games: the server merges them, because the results it
   * sends carry no times for the client to interleave. Leave `opponents` out
   * to count games against bots and people alike. A bot answers 404.
   */
  playerStats(
    id: string,
    options: { mode?: string; source?: string; opponents?: string } = {},
    signal?: AbortSignal,
  ): Promise<PublicStatsResponse> {
    const query = new URLSearchParams();
    if (options.mode !== undefined) query.set('mode', options.mode);
    if (options.source !== undefined) query.set('source', options.source);
    if (options.opponents !== undefined) query.set('opponents', options.opponents);
    const search = query.toString();
    return this.request(`/api/v1/profiles/${id}/stats${search === '' ? '' : `?${search}`}`, {
      signal,
    });
  }

  /*
   * Friends are account to account, so each of these answers `needs_account`
   * when either side is a guest; the Seat peek reads that as the guest line.
   */

  /**
   * Ask to be friends. Asking someone who has already asked you accepts
   * instead (`Friendships::Request`), so this is also Accept. Safe to retry:
   * an existing friendship comes back as it is.
   */
  requestFriend(playerId: string): Promise<Friend> {
    return this.request('/api/v1/friend_requests', {
      method: 'POST',
      body: { player_id: playerId },
    });
  }

  /**
   * End a friendship in any state: remove a friend, take back a request you
   * sent, or turn down one you were sent.
   */
  removeFriend(playerId: string): Promise<void> {
    return this.request(`/api/v1/friends/${playerId}`, { method: 'DELETE' });
  }

  /** Also ends a friendship between the two, in both directions. Safe to send twice. */
  block(playerId: string): Promise<void> {
    return this.request('/api/v1/blocks', { method: 'POST', body: { player_id: playerId } });
  }

  unblock(playerId: string): Promise<void> {
    return this.request(`/api/v1/blocks/${playerId}`, { method: 'DELETE' });
  }

  /** `game_id` names the table it happened at, when there was one. */
  report(report: PlayerReport): Promise<void> {
    return this.request('/api/v1/reports', { method: 'POST', body: report });
  }

  /* ----------------------------------------------------------- matchmaking */

  queue(options: { mode: string; format: string; allowBotBackfill: boolean }): Promise<Ticket> {
    return this.request('/api/v1/matchmaking/tickets', {
      method: 'POST',
      body: {
        mode: options.mode,
        format: options.format,
        allow_bot_backfill: options.allowBotBackfill,
      },
    });
  }

  ticket(id: string, signal?: AbortSignal): Promise<Ticket> {
    return this.request(`/api/v1/matchmaking/tickets/${id}`, {
      signal,
    });
  }

  cancelTicket(id: string): Promise<void> {
    return this.request(`/api/v1/matchmaking/tickets/${id}`, { method: 'DELETE' });
  }

  /* --------------------------------------------------------------- lobbies */

  /** `ruleset` carries only what the host chose; Rails fills in the rest. */
  createLobby(mode: string, format: string, ruleset?: Partial<Ruleset>): Promise<Lobby> {
    return this.request('/api/v1/lobbies', {
      method: 'POST',
      body: ruleset === undefined ? { mode, format } : { mode, format, ruleset },
    });
  }

  lobby(code: string, signal?: AbortSignal): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}`, {
      signal,
    });
  }

  /**
   * The table behind a shared link, as the reader would sit at it. Public: a
   * newcomer reads it before they agree, and so before they have a player.
   */
  lobbyInvitation(code: string, signal?: AbortSignal): Promise<LobbyInvitation> {
    return this.request(`/api/v1/lobbies/${code}/invitation`, { signal });
  }

  joinLobby(code: string): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}/join`, { method: 'POST' });
  }

  /**
   * Gets up from the table: the seat opens for the next person with the link.
   * The host leaving closes the table. Idempotent, so a retry is harmless.
   */
  leaveLobby(code: string): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}/membership`, { method: 'DELETE' });
  }

  addBot(code: string, seat: number, difficulty: string): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}/bots`, {
      method: 'POST',
      body: { seat, difficulty },
    });
  }

  removeBot(code: string, seat: number): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}/bots`, { method: 'DELETE', body: { seat } });
  }

  startLobby(code: string): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}/start`, { method: 'POST' });
  }

  /** Says one approved phrase at the table, and answers with the lobby that heard it. */
  lobbyTalk(code: string, id: LobbyTalkId): Promise<Lobby> {
    return this.request(`/api/v1/lobbies/${code}/talk`, { method: 'POST', body: { id } });
  }

  /* ----------------------------------------------------------------- games */

  game(id: string): Promise<GameSummary> {
    return this.request(`/api/v1/games/${id}`);
  }

  /** `(config, seed, moves)` re-runs the engine; `events` is for scrubbing. */
  replay(id: string): Promise<ReplayResponse> {
    return this.request(`/api/v1/games/${id}/replay`);
  }

  /** Re-issuable: an expiring token must never strand a reconnecting player. */
  connectToken(gameId: string): Promise<ConnectTokenResponse> {
    return this.request(`/api/v1/games/${gameId}/connect_token`, { method: 'POST' });
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * In the browser the API is same-origin behind a dev proxy; in a Capacitor
 * shell there is no origin to speak of, so the build supplies an absolute URL.
 */
export function inferBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured !== undefined && configured !== '') return configured;
  return '';
}

export const api = new ApiClient();
